import React from 'react';
import { useLoaderData } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { AppRouter } from '@/routes/AppRouter';
import { useOrgStore } from '@/stores/orgStore';
import { useI18n } from '@/hooks/useI18n';
import { useOrgTheme } from '@/hooks/useOrgTheme';
import { applyServerThemePreference } from '@/lib/theme';
import { Spinner } from '@/components/ui';
import { NoOrganizationAccess } from '@/components/organization/NoOrganizationAccess';
import { shouldShowNoOrgScreen } from '@/lib/orgGuard';

type User = {
  id: string;
  email: string;
  fullName: string;
  themePreference: 'light' | 'dark' | 'system';
  isEmailVerified: boolean;
  isSuperAdmin: boolean;
};

export function ProtectedApp() {
  const loadedUser = useLoaderData() as User | undefined;
  if (process.env.NODE_ENV === 'development') console.log('[ProtectedApp] Rendered with user:', loadedUser?.id);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') console.log('[ProtectedApp] useEffect triggered, loadedUser:', loadedUser?.id);
    if (loadedUser) {
      const current = useAuthStore.getState().user;
      if (process.env.NODE_ENV === 'development') console.log('[ProtectedApp] Current store user:', current?.id);
      if (!current || current.id !== loadedUser.id) {
        if (process.env.NODE_ENV === 'development') console.log('[ProtectedApp] Updating auth store with new user');
        // B10: Apply server-persisted theme preference on every session bootstrap
        // (page reload, new tab, direct URL). Ensures server value always wins,
        // even when the user bypasses the login form (e.g., cookie-based restore).
        applyServerThemePreference(loadedUser.themePreference ?? 'system');
        useAuthStore.setState({ user: loadedUser });
      }
      // Note: Org data should be initialized from bootstrap in main.tsx router loader
      // This ensures we don't make redundant API calls
    }
  }, [loadedUser]);

  // Initialize i18n: loads dictionary, checks version on route changes
  useI18n();

  // Apply org theme (CSS variable overrides) when active org changes
  useOrgTheme();

  // Polish Wave 6 Fix Wave 4 — org-context guard
  // Read org store state for the no-org screen decision.
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const orgIsLoading = useOrgStore((s) => s.isLoading);
  const orgInitialized = useOrgStore((s) => s.initialized);
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  // While org store is still hydrating, show a spinner — don't flash the no-org screen.
  if (!orgInitialized || orgIsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" label="Loading..." />
      </div>
    );
  }

  // A non-super-admin user with zero active org memberships gets a clear screen.
  if (shouldShowNoOrgScreen({ initialized: orgInitialized, isLoading: orgIsLoading, orgsCount: orgs.length, isSuperAdmin })) {
    return <NoOrganizationAccess />;
  }

  // Suppress unused variable warning — activeOrgId is read here to make dependency clear
  void activeOrgId;

  return <AppRouter />;
}


